import { DiscoveryLevel, TopicSearchResult } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, undefinedIfEmpty } from '../helpers/utils';

import { TextField } from '@material/mwc-textfield';
import { getTopicsforBackpack } from '../helpers/api/topics-api';

@customElement('add-maps-modal')
export class AddMapsModal extends LitElement {
    private readonly amplitude_callsite: string = 'Add Maps Modal';

    @query('#searchText')
    private readonly searchTextElement!: TextField;

    @property({ type: Array })
    selectedTopics: TopicSearchResult[] = [];

    @state()
    allTopics: TopicSearchResult[] = [];

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    numPrivate: number = 0;

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const topics = (await getTopicsforBackpack(this.amplitude_callsite)).filter((topic) => topic.is_owner || topic.is_writer);
        this.allTopics = topics;
        this.topics = topics;
    }

    private readonly onSearchText = debounce(this.searchTextInternal, 200);

    private searchTextInternal() {
        const searchText = undefinedIfEmpty(this.searchTextElement.value?.trim().toLocaleLowerCase());
        if (searchText) {
            this.topics = this.allTopics.filter(topic => {
                return topic.name.toLocaleLowerCase().includes(searchText);
            });
        } else {
            this.topics = this.allTopics;
        }
    }

    private cleanUpState() {
        this.selectedTopics = [];
        this.numPrivate = 0;
    }

    private handleSave() {
        this.dispatchEvent(new CustomEvent('add-topics-to-profile', {
            detail: {
                selectedTopics: this.selectedTopics
            }
        }));

        this.cleanUpState();
        this.dispatchEvent(new CustomEvent('close'));
}

    private handleCancel() {
        this.cleanUpState();
        this.dispatchEvent(new CustomEvent('close'));
    }

    private handleSelectTopic(addTopic: boolean, topic: TopicSearchResult) {
        if (addTopic) {
            this.selectedTopics = [...this.selectedTopics, topic];
        } else {
            this.selectedTopics = this.selectedTopics.filter((selectedTopic) => selectedTopic._id.$oid !== topic._id.$oid);
        }

        const privateMaps = this.selectedTopics.filter(topic => topic.discovery_level === DiscoveryLevel.Private);

        this.numPrivate = privateMaps.length;
    }

    render() {
        return html`
            <div class="add-modal__wrapper">
                <h2 class="add-modal__title">
                    Add Maps to Page
                </h2>
                <mwc-textfield
                    id="searchText"
                    class="add-modal__search"
                    helper="Search for your map"
                    icon="search"
                    outlined
                    @input="${this.onSearchText}">
                </mwc-textfield>
                <div class="add-modal__topics">
                    ${this.topics?.map(topic => {
                        return html`
                            <form-checkbox
                                class="add-modal__topic"
                                ?checked="${this.selectedTopics.find(selectedTopic => selectedTopic._id.$oid === topic._id?.$oid)}"
                                @change="${(e: any) => this.handleSelectTopic((e.detail as HTMLInputElement).checked, topic)}">
                                    <span>${topic.name}</span>
                            </form-checkbox>`;
                    })}
                </div>
                ${this.numPrivate > 0 ?
                    html`<p class="add-modal__warning">${this.numPrivate} maps you are adding are currently private. Adding them to your Creator Profile will publish them to the public.</p>`
                    : ''
                }
                <div class="add-modal__actions">
                    <form-button class="add-modal__action" variant="social" @click="${this.handleCancel}">Cancel</form-button>
                    <form-button class="add-modal__action" @click="${this.handleSave}" ?disabled=${this.selectedTopics.length === 0}>
                        Add ${this.selectedTopics.length} Maps
                    </form-button>
                </div>
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    static get styles() {
        return css`
            .add-modal__wrapper {
                padding: 0.5rem 1.5rem 1rem;
            }

            .add-modal__title {
                margin-bottom: 1.25rem;
                color: var(--main-primary-color);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .add-modal__search {
                width: 100%;
            }

            .add-modal__warning {
                color: red;
            }

            .add-modal__input {
                width: 550px;
                max-width: 100%;
                display: block;
            }

            .add-modal__topics {
                height: 300px;
                overflow: scroll;
            }

            .add-modal__topic {
                box-shadow: rgb(31 27 45 / 8%) 0px 2px 2px -2px, rgb(31 27 45 / 8%) 0px 4px 12px;
            }

            .add-modal__action {
                margin-right: 4px;
            }

            .add-modal__actions {
                margin-top: 8px;
                display: flex;
                justify-content: space-between;
            }
        `;
    }
}
